import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { EvaluationService } from "../../services/EvaluationService";
import { ApplicationService } from "../../services/ApplicationService";
import { IntentService } from "../../services/IntentService";
import { SentinelService } from "../../services/SentinelService";
import { Application, ApplicationAction } from "../../model_gen/application";
import { IntentGroup } from "../../model_gen/intent";
import { EvaluateResponse } from "../../model_gen/evaluation";
import { Sentinel } from "../../model_gen/sentinel";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Skeleton } from "../ui/skeleton";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import { 
  FilterIcon,
  XIcon
} from "lucide-react";
import EvaluationsTable from "./EvaluationsTable";

interface Filter {
  key: string;
  value: string | number;
  label: string;
}

const Evaluations: React.FC = () => {
  const { apiKey } = usePermissions();
  const [evaluations, setEvaluations] = useState<EvaluateResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingFilters, setIsLoadingFilters] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEvaluations, setTotalEvaluations] = useState(0);
  const [activeFilters, setActiveFilters] = useState<Filter[]>([]);
  
  // Filter options
  const [applications, setApplications] = useState<Application[]>([]);
  const [intentGroups, setIntentGroups] = useState<IntentGroup[]>([]);
  const [intents, setIntents] = useState<ApplicationAction[]>([]);
  const [sentinels, setSentinels] = useState<Sentinel[]>([]);
  
  // Filter UI state
  const [showFilters, setShowFilters] = useState(false);
  const [filterType, setFilterType] = useState<string>("");
  const [filterValue, setFilterValue] = useState<string>("");
  const [sentinelStatus, setSentinelStatus] = useState<string>("ANY");
  const [searchTerm, setSearchTerm] = useState<string>("");

  const pageSize = 10;
  const navigate = useNavigate();

  useEffect(() => {
    if (apiKey) {
      loadEvaluations();
      loadFilterOptions();
    }
  }, [apiKey, currentPage, activeFilters]);

  const loadEvaluations = () => {
    const offset = (currentPage - 1) * pageSize;
    EvaluationService.getAllEvaluationResults(
      offset,
      pageSize,
      activeFilters,
      setIsLoading,
      apiKey
    ).subscribe(
      (response: any) => {
        setEvaluations(response.items || []);
        setTotalEvaluations(response.total || 0);
      },
      (error: any) => {
        console.error("Error loading evaluations:", error);
        setIsLoading(false);
      }
    );
  };

  const loadFilterOptions = () => {
    setIsLoadingFilters(true);
    let completedRequests = 0;
    const totalRequests = 3;

    const checkAllComplete = () => {
      completedRequests++;
      if (completedRequests === totalRequests) {
        setIsLoadingFilters(false);
      }
    };

    // Load applications (+ intents) in one call
    ApplicationService.getApplications(null, apiKey, true).subscribe(
      (apps: Application[]) => {
        setApplications(apps);

        const allIntents: ApplicationAction[] = [];
        apps.forEach((app) => {
          if (app.action_types) {
            allIntents.push(...app.action_types);
          }
        });
        setIntents(allIntents);

        checkAllComplete();
      },
      (error: any) => {
        console.error("Error loading applications/intents:", error);
        checkAllComplete();
      }
    );

    // Load intent groups
    IntentService.getIntentGroups(0, 1000, null, apiKey).subscribe(
      (response: any) => {
        setIntentGroups(response.intent_groups || []);
        checkAllComplete();
      },
      (error: any) => {
        console.error("Error loading intent groups:", error);
        checkAllComplete();
      }
    );

    // Load sentinels
    SentinelService.getSimpleSentinels(0, 1000, () => {}, apiKey).subscribe(
      (response: any) => {
        setSentinels(response.sentinels || []);
        checkAllComplete();
      },
      (error: any) => {
        console.error("Error loading sentinels:", error);
        checkAllComplete();
      }
    );
  };

  const handleAddFilter = () => {
    if (!filterType || !filterValue) return;

    let label = "";
    let value: string | number = filterValue;

    switch (filterType) {
      case "status":
        label = `Status: ${filterValue}`;
        break;
      case "application":
        const app = applications.find(a => a.id?.toString() === filterValue);
        label = `Application: ${app?.application_name || filterValue}`;
        value = parseInt(filterValue);
        break;
      case "intent":
        const intent = intents.find(i => i.id?.toString() === filterValue);
        label = `Intent: ${intent?.action_type || filterValue}`;
        value = parseInt(filterValue);
        break;
      case "intent_group":
        const group = intentGroups.find(g => g.id?.toString() === filterValue);
        label = `Intent Group: ${group?.intent_name || filterValue}`;
        value = parseInt(filterValue);
        break;
      case "sentinel":
        const sentinel = sentinels.find(s => s.id?.toString() === filterValue);
        const statusLabel = sentinelStatus === "ANY" ? "" : ` (${sentinelStatus})`;
        label = `Sentinel: ${sentinel?.sentinel_name || filterValue}${statusLabel}`;
        value = sentinelStatus === "ANY" ? parseInt(filterValue) : `${filterValue}:${sentinelStatus}`;
        break;
      default:
        return;
    }

    const newFilter: Filter = {
      key: filterType,
      value,
      label
    };

    // Don't add duplicate filters
    const exists = activeFilters.some(f => f.key === filterType && f.value === value);
    if (!exists) {
      setActiveFilters([...activeFilters, newFilter]);
      setCurrentPage(1); // Reset to first page when adding filters
    }

    // Reset filter form
    setFilterType("");
    setFilterValue("");
    setSentinelStatus("ANY");
    setShowFilters(false);
  };

  const handleRemoveFilter = (filterToRemove: Filter) => {
    setActiveFilters(activeFilters.filter(f => 
      !(f.key === filterToRemove.key && f.value === filterToRemove.value)
    ));
    setCurrentPage(1);
  };

  const handleClearAllFilters = () => {
    setActiveFilters([]);
    setCurrentPage(1);
  };

  const handleToggleSentinelFilter = (sentinelId: number, sentinelName: string, status?: string) => {
    // Check if any filter exists for this sentinel (with any status)
    const existingFilter = activeFilters.find(f => 
      f.key === "sentinel" && 
      (f.value === sentinelId || (typeof f.value === "string" && f.value.startsWith(`${sentinelId}:`)))
    );
    
    if (existingFilter) {
      // Remove the filter
      setActiveFilters(activeFilters.filter(f => 
        !(f.key === "sentinel" && 
          (f.value === sentinelId || (typeof f.value === "string" && f.value.startsWith(`${sentinelId}:`))))
      ));
    } else {
      // Add the filter with the specified status (or ANY if not provided)
      const filterStatus = status || "ANY";
      const filterValue = filterStatus === "ANY" ? sentinelId : `${sentinelId}:${filterStatus}`;
      const statusLabel = filterStatus === "ANY" ? "" : ` (${filterStatus})`;
      
      const newFilter: Filter = {
        key: "sentinel",
        value: filterValue,
        label: `Sentinel: ${sentinelName}${statusLabel}`
      };
      setActiveFilters([...activeFilters, newFilter]);
    }
    setCurrentPage(1);
  };

  const handleFilterBadgeClick = (filter: Filter) => {
    // Parse the filter to populate the form
    setFilterType(filter.key);
    
    if (filter.key === "sentinel") {
      const value = filter.value.toString();
      if (value.includes(":")) {
        const [sentinelId, status] = value.split(":");
        setFilterValue(sentinelId);
        setSentinelStatus(status);
      } else {
        setFilterValue(value);
        setSentinelStatus("ANY");
      }
    } else {
      setFilterValue(filter.value.toString());
      setSentinelStatus("ANY");
    }
    
    // Remove the filter from active filters
    handleRemoveFilter(filter);
    
    // Show the filter form
    setShowFilters(true);
  };

  const getFilterOptions = () => {
    switch (filterType) {
      case "status":
        return [
          { value: "PASS", label: "Pass" },
          { value: "FAULT", label: "Fault" },
          { value: "ERROR", label: "Error" },
          { value: "NA", label: "N/A" }
        ];
      case "application":
        return applications.map(app => ({
          value: app.id?.toString() || "",
          label: app.application_name || ""
        }));
      case "intent":
        return intents.map(intent => ({
          value: intent.id?.toString() || "",
          label: intent.action_type || ""
        }));
      case "intent_group":
        return intentGroups.map(group => ({
          value: group.id?.toString() || "",
          label: group.intent_name || ""
        }));
      case "sentinel":
        return sentinels.map(sentinel => ({
          value: sentinel.id?.toString() || "",
          label: sentinel.sentinel_name || ""
        }));
      default:
        return [];
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Home
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">Evaluations</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                Evaluations
              </h1>
              <p className="text-lg text-muted-foreground font-gc-reg">
                View and analyze evaluation results across your applications
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Filters Section */}
        <div className="space-y-4">
          {/* Filter Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {isLoadingFilters ? (
                <Skeleton className="h-10 w-32" />
              ) : (
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className="gap-2"
                >
                  <FilterIcon className="w-4 h-4" />
                  Add Filter
                </Button>
              )}
              
              {activeFilters.length > 0 && (
                <Button
                  variant="ghost"
                  onClick={handleClearAllFilters}
                  className="gap-2 text-muted-foreground hover:text-foreground"
                >
                  <XIcon className="w-4 h-4" />
                  Clear All
                </Button>
              )}
            </div>
            
            {/* No filters message - centered */}
            {!isLoadingFilters && activeFilters.length === 0 && !showFilters && (
              <div className="flex-1 flex justify-center">
                <p className="text-sm text-muted-foreground">
                  No filters applied. Click "Add Filter" to filter your evaluations.
                </p>
              </div>
            )}
            
            {/* Spacer to balance the layout when no message is shown */}
            {(isLoadingFilters || activeFilters.length > 0 || showFilters) && (
              <div className="flex-1"></div>
            )}
          </div>

          {/* Active Filters */}
          {activeFilters.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-gc-med text-muted-foreground">Active Filters:</p>
              <div className="flex flex-wrap gap-2">
                {activeFilters.map((filter, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="gap-2 px-3 py-1 cursor-pointer hover:bg-accent transition-colors"
                    onClick={() => handleFilterBadgeClick(filter)}
                  >
                    {filter.label}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFilter(filter);
                      }}
                      className="hover:text-destructive transition-colors"
                    >
                      <XIcon className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Filter Form */}
          {showFilters && (
            <Card className="border-border bg-muted/20">
              <CardContent className="p-6">
                {isLoadingFilters ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-10 w-full" />
                    </div>
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-12" />
                      <Skeleton className="h-10 w-full" />
                    </div>
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-16" />
                      <div className="flex gap-2">
                        <Skeleton className="h-10 flex-1" />
                        <Skeleton className="h-10 w-20" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={`grid grid-cols-1 gap-4 ${filterType === "sentinel" ? "md:grid-cols-4" : "md:grid-cols-3"}`}>
                    <div className="space-y-2">
                      <label className="text-sm font-gc-med text-foreground">
                        Filter Type
                      </label>
                      <Select value={filterType} onValueChange={setFilterType}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select filter type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="status">Status</SelectItem>
                          <SelectItem value="application">Application</SelectItem>
                          <SelectItem value="intent">Intent</SelectItem>
                          <SelectItem value="intent_group">Intent Group</SelectItem>
                          <SelectItem value="sentinel">Sentinel</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-gc-med text-foreground">
                        Value
                      </label>
                      <Select 
                        value={filterValue} 
                        onValueChange={setFilterValue}
                        disabled={!filterType}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {getFilterOptions().map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {filterType === "sentinel" && (
                      <div className="space-y-2">
                        <label className="text-sm font-gc-med text-foreground">
                          Status
                        </label>
                        <Select 
                          value={sentinelStatus} 
                          onValueChange={setSentinelStatus}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ANY">Any Status</SelectItem>
                            <SelectItem value="PASS">Pass</SelectItem>
                            <SelectItem value="FAULT">Fault</SelectItem>
                            <SelectItem value="ERROR">Error</SelectItem>
                            <SelectItem value="NA">N/A</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="space-y-2">
                      <label className="text-sm font-gc-med text-foreground">
                        Action
                      </label>
                      <div className="flex gap-2">
                        <Button
                          onClick={handleAddFilter}
                          disabled={!filterType || !filterValue}
                          className="flex-1"
                        >
                          Add Filter
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setShowFilters(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}


        </div>

        {/* Evaluations Table */}
        <EvaluationsTable
          evaluations={evaluations}
          totalEvaluations={totalEvaluations}
          currentPage={currentPage}
          pageSize={pageSize}
          isLoading={isLoading}
          onPageChange={setCurrentPage}
          selectedSentinels={new Set(activeFilters.filter(f => f.key === "sentinel").map(f => {
            const value = f.value.toString();
            return value.includes(":") ? Number(value.split(":")[0]) : Number(value);
          }))}
          onToggleSentinelFilter={handleToggleSentinelFilter}
          apiKey={apiKey}
        />
      </div>
    </div>
  );
};

export default Evaluations; 